<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>企业创作者审核</span>
        <div class="header-actions">
          <el-input
            v-model="keyword"
            placeholder="搜索昵称/手机号/公司名"
            clearable
            class="search-input"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-select v-model="filterStatus" placeholder="全部状态" clearable style="width: 140px" @change="handleSearch">
            <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </div>
      </div>

      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column prop="userId" label="用户ID" width="80" />
        <el-table-column label="申请人" min-width="120">
          <template slot-scope="{ row }">{{ row.nickname || `用户${row.userId}` }}</template>
        </el-table-column>
        <el-table-column label="手机号" width="130">
          <template slot-scope="{ row }">{{ maskPhone(row.phone) }}</template>
        </el-table-column>
        <el-table-column prop="companyName" label="公司名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="orgCode" label="机构编码" min-width="150" show-overflow-tooltip />
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="statusTagType(row.enterpriseAuditStatus)">
              {{ labelOf(ENTERPRISE_AUDIT_STATUS, row.enterpriseAuditStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="申请时间" min-width="120">
          <template slot-scope="{ row }">{{ formatUserDate(row.enterpriseApplyAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
            <template v-if="row.enterpriseAuditStatus === 1">
              <el-button type="text" size="small" @click="handleAudit(row, 2)">通过</el-button>
              <el-button type="text" size="small" class="danger" @click="openReject(row)">驳回</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="handlePageChange"
      />
    </el-card>

    <el-dialog title="申请详情" :visible.sync="detailVisible" width="640px">
      <div v-if="detail" class="detail-body">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="用户ID">{{ detail.userId }}</el-descriptions-item>
          <el-descriptions-item label="昵称">{{ detail.nickname || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ maskPhone(detail.phone) }}</el-descriptions-item>
          <el-descriptions-item label="申请状态">
            <el-tag size="mini" :type="statusTagType(detail.enterpriseAuditStatus)">
              {{ labelOf(ENTERPRISE_AUDIT_STATUS, detail.enterpriseAuditStatus) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="公司名称" :span="2">{{ detail.companyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="机构编码" :span="2">{{ detail.orgCode || '-' }}</el-descriptions-item>
          <el-descriptions-item label="法人姓名">{{ detail.realName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="法人身份证">{{ maskIdCard(detail.idCardNo) }}</el-descriptions-item>
          <el-descriptions-item label="申请时间" :span="2">{{ formatUserDate(detail.enterpriseApplyAt) }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.enterpriseAuditRemark" label="审核备注" :span="2">
            {{ detail.enterpriseAuditRemark }}
          </el-descriptions-item>
        </el-descriptions>
        <div v-if="detail.businessLicenseUrl" class="license-block">
          <div class="license-label">营业执照</div>
          <el-image
            class="license-img"
            :src="resolveFileUrl(detail.businessLicenseUrl)"
            :preview-src-list="[resolveFileUrl(detail.businessLicenseUrl)]"
            fit="contain"
          />
        </div>
      </div>
      <div v-if="detail && detail.enterpriseAuditStatus === 1" slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="danger" plain @click="openReject(detail)">驳回</el-button>
        <el-button type="primary" @click="handleAudit(detail, 2)">通过审核</el-button>
      </div>
    </el-dialog>

    <el-dialog title="驳回申请" :visible.sync="rejectVisible" width="480px">
      <el-input
        v-model="rejectRemark"
        type="textarea"
        :rows="4"
        maxlength="200"
        show-word-limit
        placeholder="请填写驳回原因，将展示给用户"
      />
      <div slot="footer">
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" :loading="auditing" @click="confirmReject">确认驳回</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getEnterpriseCreatorApplies, auditEnterpriseCreator } from '@/api/admin'
import { ENTERPRISE_AUDIT_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'
import { formatUserDate, maskPhone } from '@/utils/userFormat'
import listPage from '@/mixins/listPage'

function maskIdCard(value) {
  if (!value) return '-'
  const s = String(value)
  if (s.length < 8) return s
  return `${s.slice(0, 4)}****${s.slice(-4)}`
}

function resolveFileUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  const base = process.env.VUE_APP_API_BASE || ''
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`
}

export default {
  name: 'UsersEnterpriseAudit',
  mixins: [listPage],
  data() {
    return {
      ENTERPRISE_AUDIT_STATUS,
      keyword: '',
      filterStatus: 1,
      detailVisible: false,
      detail: null,
      rejectVisible: false,
      rejectRemark: '',
      rejectTarget: null,
      auditing: false
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(ENTERPRISE_AUDIT_STATUS).filter(opt => opt.value !== 0)
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    labelOf,
    formatUserDate,
    maskPhone,
    maskIdCard,
    resolveFileUrl,
    statusTagType(status) {
      if (status === 2) return 'success'
      if (status === 3) return 'danger'
      return 'warning'
    },
    fetchList(params) {
      return getEnterpriseCreatorApplies({
        ...params,
        keyword: this.keyword || undefined,
        auditStatus: this.filterStatus != null ? this.filterStatus : undefined
      })
    },
    openDetail(row) {
      this.detail = { ...row }
      this.detailVisible = true
    },
    handleAudit(row, auditStatus) {
      const action = auditStatus === 2 ? '通过' : '驳回'
      const name = row.companyName || row.nickname || `用户${row.userId}`
      this.$confirm(`确定${action}「${name}」的企业创作者申请？`, '审核确认', {
        type: auditStatus === 2 ? 'success' : 'warning'
      }).then(async () => {
        await auditEnterpriseCreator(row.userId, { auditStatus })
        this.$message.success(`已${action}`)
        this.detailVisible = false
        this.loadList()
      }).catch(() => {})
    },
    openReject(row) {
      this.rejectTarget = row
      this.rejectRemark = ''
      this.rejectVisible = true
    },
    async confirmReject() {
      if (!this.rejectRemark.trim()) {
        this.$message.warning('请填写驳回原因')
        return
      }
      this.auditing = true
      try {
        await auditEnterpriseCreator(this.rejectTarget.userId, {
          auditStatus: 3,
          remark: this.rejectRemark.trim()
        })
        this.$message.success('已驳回')
        this.rejectVisible = false
        this.detailVisible = false
        this.loadList()
      } finally {
        this.auditing = false
      }
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.search-input {
  width: 220px;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
.danger {
  color: #f56c6c;
}
.detail-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.license-block {
  margin-top: 4px;
}
.license-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}
.license-img {
  width: 100%;
  max-height: 320px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
}
</style>
